import React, { useState } from 'react';
import { FaLightbulb, FaChartPie, FaRocket } from 'react-icons/fa';

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const features: Feature[] = [
  {
    title: 'Business Idea Generator',
    description: 'Generate unique business ideas tailored to your strengths and dreams.',
    icon: <FaLightbulb className="text-4xl" />,
    color: 'from-yellow-400 to-orange-500'
  },
  {
    title: 'Business Plan Generator',
    description: 'Craft a detailed, investor-ready business plan in minutes.',
    icon: <FaChartPie className="text-4xl" />,
    color: 'from-blue-400 to-indigo-500'
  },
  {
    title: 'Launchpad',
    description: 'Follow actionable checklists from idea to launch and beyond.',
    icon: <FaRocket className="text-4xl" />,
    color: 'from-purple-400 to-pink-500'
  },
];

const FeatureGrid: React.FC = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background with pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white opacity-50 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(120,120,255,0.08)_0,_rgba(255,255,255,0)_50%)] bg-[length:20px_20px]"></div>
      </div>
      
      {/* Content */}
      <div className="container relative mx-auto px-4 text-center z-10">
        <div className="relative inline-block mb-14">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">Core Modules</h2>
          <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mx-auto"></div>
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {features.map((feature, index) => (
            <div 
              key={feature.title} 
              className="relative group"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* 3D Card with glass morphism */}
              <div 
                className={`
                  relative p-8 rounded-2xl overflow-hidden transition-all duration-500 z-10
                  bg-white/30 backdrop-filter backdrop-blur-lg border border-white/20
                  hover:shadow-[0_20px_50px_rgba(8,_112,_184,_0.3)]
                  ${hoveredIndex === index ? 'scale-105 -translate-y-2' : 'scale-100'}
                `}
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                
                {/* Icon with floating effect */}
                <div 
                  className={`
                    relative mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6
                    text-white transition-all duration-500 z-20
                    bg-gradient-to-br ${feature.color} shadow-lg group-hover:shadow-xl
                    ${hoveredIndex === index ? 'scale-110' : 'scale-100'}
                  `}
                  style={{
                    transform: `translateZ(${hoveredIndex === index ? '20px' : '0px'})`,
                    animation: 'float 4s ease-in-out infinite'
                  }}
                >
                  {feature.icon}
                </div>
                
                {/* Title with 3D transform */}
                <h3 
                  className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-blue-600 transition-all duration-300"
                  style={{
                    transform: `translateZ(${hoveredIndex === index ? '15px' : '0px'})`,
                  }}
                >
                  {feature.title}
                </h3>
                
                {/* Description */}
                <p 
                  className="text-gray-600 group-hover:text-gray-800 transition-all duration-300"
                  style={{
                    transform: `translateZ(${hoveredIndex === index ? '10px' : '0px'})`,
                  }}
                >
                  {feature.description}
                </p>
                
                {/* Hover indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-blue-500 flex items-center">
                    <span className="mr-1 text-sm">Explore</span>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Shadow below */}
              <div 
                className={`
                  absolute inset-x-0 -bottom-3 h-12 rounded-2xl bg-black/5 blur-lg transition-all duration-500
                  ${hoveredIndex === index ? 'opacity-80 scale-95' : 'opacity-40 scale-90'}
                `}
                style={{
                  zIndex: 0,
                  transformOrigin: 'center top'
                }}
              ></div>
            </div>
          ))}
        </div>
        
        {/* AI Business Consultants Section */}
        <div className="mt-24">
          <div className="relative inline-block mb-14">
            <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">AI Business Consultants</h2>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mx-auto"></div>
          </div>
          
          <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10">
            Our AI consultants analyze your platform data to provide personalized, data-driven advice specific to your business needs and goals.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Marketing Consultant */}
            <div className="relative group h-full flex flex-col">
              <div className="
                relative p-8 rounded-2xl overflow-hidden transition-all duration-500 z-10
                bg-white/30 backdrop-filter backdrop-blur-lg border border-white/20
                hover:shadow-[0_20px_50px_rgba(255,_107,_107,_0.3)]
                group-hover:scale-105 group-hover:-translate-y-2
                flex flex-col h-full
              ">
                <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-500 opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
                
                <div className="
                  relative mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6
                  text-white transition-all duration-500 z-20
                  bg-gradient-to-br from-red-400 to-red-500 shadow-lg group-hover:shadow-xl
                ">
                  <span className="text-2xl font-bold">K</span>
                </div>
                
                <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-red-600 transition-all duration-300">
                  Keith
                </h3>
                
                <p className="text-gray-700 font-medium mb-2">Marketing Specialist</p>
                
                <p className="text-gray-600 group-hover:text-gray-800 transition-all duration-300 flex-grow">
                  Leverages your customer data to develop tailored marketing strategies, audience targeting, and campaigns based on your business's unique strengths and market position.
                </p>
              </div>
              
              <div className="
                absolute inset-x-0 -bottom-3 h-12 rounded-2xl bg-black/5 blur-lg transition-all duration-500
                opacity-40 scale-90 group-hover:opacity-80 group-hover:scale-95
              " style={{ zIndex: 0, transformOrigin: 'center top' }}></div>
            </div>

            {/* Financial Consultant */}
            <div className="relative group h-full flex flex-col">
              <div className="
                relative p-8 rounded-2xl overflow-hidden transition-all duration-500 z-10
                bg-white/30 backdrop-filter backdrop-blur-lg border border-white/20
                hover:shadow-[0_20px_50px_rgba(66,_99,_235,_0.3)]
                group-hover:scale-105 group-hover:-translate-y-2
                flex flex-col h-full
              ">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-500 opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
                
                <div className="
                  relative mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6
                  text-white transition-all duration-500 z-20
                  bg-gradient-to-br from-blue-400 to-blue-500 shadow-lg group-hover:shadow-xl
                ">
                  <span className="text-2xl font-bold">C</span>
                </div>
                
                <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-blue-600 transition-all duration-300">
                  Carol
                </h3>
                
                <p className="text-gray-700 font-medium mb-2">Financial Advisor</p>
                
                <p className="text-gray-600 group-hover:text-gray-800 transition-all duration-300 flex-grow">
                  Analyzes your financial data to provide customized guidance on cash flow optimization, investment priorities, and growth strategies specific to your industry and business lifecycle.
                </p>
              </div>
              
              <div className="
                absolute inset-x-0 -bottom-3 h-12 rounded-2xl bg-black/5 blur-lg transition-all duration-500
                opacity-40 scale-90 group-hover:opacity-80 group-hover:scale-95
              " style={{ zIndex: 0, transformOrigin: 'center top' }}></div>
            </div>

            {/* Business Strategy Consultant */}
            <div className="relative group h-full flex flex-col">
              <div className="
                relative p-8 rounded-2xl overflow-hidden transition-all duration-500 z-10
                bg-white/30 backdrop-filter backdrop-blur-lg border border-white/20
                hover:shadow-[0_20px_50px_rgba(136,_93,_241,_0.3)]
                group-hover:scale-105 group-hover:-translate-y-2
                flex flex-col h-full
              ">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-purple-500 opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
                
                <div className="
                  relative mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6
                  text-white transition-all duration-500 z-20
                  bg-gradient-to-br from-purple-400 to-purple-500 shadow-lg group-hover:shadow-xl
                ">
                  <span className="text-2xl font-bold">N</span>
                </div>
                
                <h3 className="text-2xl font-bold mb-3 text-gray-800 group-hover:text-purple-600 transition-all duration-300">
                  Nikita
                </h3>
                
                <p className="text-gray-700 font-medium mb-2">Business Strategist</p>
                
                <p className="text-gray-600 group-hover:text-gray-800 transition-all duration-300 flex-grow">
                  Evaluates your business metrics and market data to deliver personalized strategic recommendations, competitive positioning, and growth opportunities tailored to your specific goals.
                </p>
              </div>
              
              <div className="
                absolute inset-x-0 -bottom-3 h-12 rounded-2xl bg-black/5 blur-lg transition-all duration-500
                opacity-40 scale-90 group-hover:opacity-80 group-hover:scale-95
              " style={{ zIndex: 0, transformOrigin: 'center top' }}></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Animated decorative elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
    </section>
  );
};

export default FeatureGrid;
